/**
 * Shows provenance, lineage, access, and metadata for the selected Vault item.
 *
 * Stored files and live-linked source documents can look alike in the browser. The
 * inspector makes their owning source and effective permissions explicit while
 * delegating edits to the correct service boundary.
 */
import { useState } from 'react'
import { useQuery } from '@tanstack/react-query'
import {
  ChevronLeft,
  Crown,
  FolderTree,
  Globe,
  PanelRightClose,
  UserPlus,
  LockKeyhole,
  X,
} from 'lucide-react'

import type { NodeApiClient } from '@/app-shell/api/node-api-client'
import {
  Badge,
  Button,
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  ScrollArea,
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
  cn,
} from '@/components/ui'
import { useMediaQuery } from '@/subsystems/workspaces/hooks/use-media-query'
import { avatarColor, formatDate, formatSize, kindLabel, metadataLabel } from '../lib/format'
import { getSharing } from '../services/vault-api'
import type { ItemResponse, VaultGrant } from '../types'

interface VaultInspectorProps {
  api: NodeApiClient
  orgId: string
  vaultId: string
  entry: ItemResponse | null
  onClose: () => void
  onShare: (entry: ItemResponse) => void
}

const roleLabel = (role: string) => role === 'editor' ? 'Can edit' : role === 'viewer' ? 'Can view' : role

function Person({ grant }: { grant: VaultGrant }) {
  const label = grant.orgPrincipal ? 'Everyone at UMD' : grant.principalId ?? 'Unknown user'
  return (
    <div className="flex items-center gap-3 py-2">
      <span className={cn(
        'flex h-9 w-9 shrink-0 items-center justify-center rounded-full text-xs font-semibold uppercase',
        grant.orgPrincipal ? 'bg-muted text-muted-foreground' : avatarColor(label),
      )}>
        {grant.orgPrincipal ? <Globe className="h-4 w-4" /> : label.slice(0, 2)}
      </span>
      <span className="min-w-0 flex-1 truncate text-sm">{label}</span>
      <Badge variant="outline">{roleLabel(grant.role)}</Badge>
    </div>
  )
}

function InspectorContent({
  api,
  orgId,
  vaultId,
  entry,
  onClose,
  onShare,
  collapsible,
  onCollapse,
}: VaultInspectorProps & { entry: ItemResponse; collapsible: boolean; onCollapse?: () => void }) {
  const sharing = useQuery({
    queryKey: ['vault-sharing', orgId, vaultId, entry.item.itemId],
    queryFn: () => getSharing(api, orgId, vaultId, entry.item.itemId),
  })
  const { item } = entry
  const location = entry.breadcrumbs.length
    ? entry.breadcrumbs.map(crumb => crumb.name).join(' / ')
    : 'Vault root'

  return (
    <div className="flex h-full min-h-0 flex-col">
      <div className="flex items-start gap-2 border-b p-4">
        <div className="min-w-0 flex-1">
          <p className="truncate font-semibold">{item.name}</p>
          <p className="flex items-center gap-1 text-xs text-muted-foreground">
            {item.linked && <LockKeyhole className="h-3 w-3" />} {kindLabel(entry)}
          </p>
        </div>
        {collapsible && (
          <Button variant="ghost" size="icon" className="h-8 w-8" aria-label="Collapse details panel" onClick={onCollapse}>
            <PanelRightClose className="h-4 w-4" />
          </Button>
        )}
        <Button variant="ghost" size="icon" className="h-8 w-8" aria-label="Close details" onClick={onClose}>
          <X className="h-4 w-4" />
        </Button>
      </div>
      <Tabs defaultValue="sharing" className="flex min-h-0 flex-1 flex-col">
        <TabsList className="mx-4 mt-3 grid grid-cols-2">
          <TabsTrigger value="sharing">Sharing</TabsTrigger>
          <TabsTrigger value="details">Details</TabsTrigger>
        </TabsList>
        <ScrollArea className="min-h-0 flex-1">
          <TabsContent value="sharing" className="m-0 space-y-4 p-4">
            <div className="flex items-center gap-3 py-1">
              <span className={cn(
                'flex h-10 w-10 items-center justify-center rounded-full text-sm font-semibold uppercase',
                avatarColor(item.ownerUserId),
              )}>
                {item.ownerUserId.slice(0, 2)}
              </span>
              <div className="min-w-0 flex-1">
                <p className="truncate text-sm font-medium">{item.ownerUserId}</p>
                <p className="text-xs text-muted-foreground">Owner</p>
              </div>
              <Crown className="h-4 w-4 text-muted-foreground" />
            </div>
            {sharing.isPending && <p className="text-sm text-muted-foreground">Loading access…</p>}
            {sharing.data?.directGrants.map((grant, index) => (
              <Person key={`${grant.principalId}-${grant.orgPrincipal}-${index}`} grant={grant} />
            ))}
            {!!sharing.data?.inheritedGrants.length && (
              <div className="border-t pt-3">
                <p className="mb-1 text-xs font-medium uppercase tracking-wide text-muted-foreground">
                  Inherited from parent folders
                </p>
                {sharing.data.inheritedGrants.map((grant, index) => (
                  <Person key={`${grant.resourceId}-${grant.principalId}-${index}`} grant={grant} />
                ))}
              </div>
            )}
            {entry.effectiveRole === 'owner' && (
              <Button variant="outline" className="w-full" onClick={() => onShare(entry)}>
                <UserPlus className="mr-2 h-4 w-4" /> Manage access
              </Button>
            )}
          </TabsContent>
          <TabsContent value="details" className="m-0 space-y-5 p-4 text-sm">
            <div>
              <p className="text-xs text-muted-foreground">Owner</p>
              <p className="mt-1 break-words">{item.ownerUserId}</p>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <p className="text-xs text-muted-foreground">Created</p>
                <p className="mt-1">{formatDate(item.createdAt)}</p>
              </div>
              <div>
                <p className="text-xs text-muted-foreground">Modified</p>
                <p className="mt-1">{formatDate(item.updatedAt)}</p>
              </div>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <p className="text-xs text-muted-foreground">Size</p>
                <p className="mt-1">{item.kind === 'file' ? formatSize(item.sizeBytes) : '—'}</p>
              </div>
              <div>
                <p className="text-xs text-muted-foreground">Your access</p>
                <p className="mt-1">{roleLabel(entry.effectiveRole)}</p>
              </div>
            </div>
            <div>
              <p className="text-xs text-muted-foreground">Location</p>
              <p className="mt-1 flex items-start gap-2 break-words">
                <FolderTree className="mt-0.5 h-4 w-4 shrink-0" /> {location}
              </p>
            </div>
            {item.mimeType && (
              <div>
                <p className="text-xs text-muted-foreground">Content type</p>
                <p className="mt-1 break-all">{item.mimeType}</p>
              </div>
            )}
            {item.linked && (
              <div className="rounded-lg border bg-muted/40 p-3">
                <p className="flex items-center gap-2 text-xs font-medium"><LockKeyhole className="h-3.5 w-3.5" /> Linked workspace content</p>
                <p className="mt-1 text-xs text-muted-foreground">Live read-only view of {item.sourceWorkspaceId}. Edit or delete it in the source application.</p>
              </div>
            )}
            {item.kind === 'file' && Object.keys(item.metadata).length > 0 && (
              <div>
                <p className="text-xs text-muted-foreground">Metadata</p>
                <dl className="mt-2 space-y-1 rounded-lg border p-3 text-xs">
                  {Object.entries(item.metadata).map(([key, value]) => (
                    <div key={key} className="grid grid-cols-[minmax(0,1fr)_minmax(0,1fr)] gap-2">
                      <dt className="truncate font-medium">{metadataLabel(key)}</dt>
                      <dd className="break-all text-muted-foreground">{value}</dd>
                    </div>
                  ))}
                </dl>
              </div>
            )}
          </TabsContent>
        </ScrollArea>
      </Tabs>
    </div>
  )
}

export function VaultInspector(props: VaultInspectorProps) {
  const desktop = useMediaQuery('(min-width: 1024px)')
  const [collapsedItemId, setCollapsedItemId] = useState<string | null>(null)
  if (!props.entry) return null
  const collapsed = collapsedItemId === props.entry.item.itemId
  const close = () => {
    setCollapsedItemId(null)
    props.onClose()
  }

  if (!desktop) {
    return (
      <Dialog open onOpenChange={(open) => { if (!open) close() }}>
        <DialogContent
          showCloseButton={false}
          className="flex h-[min(42rem,calc(100dvh-2rem))] max-w-[calc(100vw-2rem)] flex-col gap-0 overflow-hidden p-0 sm:max-w-md"
        >
          <DialogHeader className="sr-only"><DialogTitle>Item details</DialogTitle></DialogHeader>
          <InspectorContent {...props} entry={props.entry} onClose={close} collapsible={false} />
        </DialogContent>
      </Dialog>
    )
  }

  if (collapsed) {
    return (
      <aside className="sticky top-4 flex h-12 w-12 shrink-0 items-center justify-center rounded-lg border bg-card">
        <Button variant="ghost" size="icon" aria-label="Expand details panel" onClick={() => setCollapsedItemId(null)}>
          <ChevronLeft className="h-4 w-4" />
        </Button>
      </aside>
    )
  }

  return (
    <aside className="sticky top-4 h-[calc(100vh-8rem)] w-80 shrink-0 overflow-hidden rounded-lg border bg-card shadow-sm">
      <InspectorContent
        {...props}
        entry={props.entry}
        onClose={close}
        collapsible
        onCollapse={() => setCollapsedItemId(props.entry!.item.itemId)}
      />
    </aside>
  )
}
